import {
  type CollectionDto,
  copyCollectionApi,
  createCollectionApi,
  deleteCollectionApi,
  getArticleCollectionsApi,
  putCollectionArticleApi,
  removeCollectionArticleApi,
  updateCollectionApi,
} from "../api";
import { toArticleKey } from "../collections";
import { type BookmarkedArticles, items } from "./items";

const mapCollection = (collections: CollectionDto[], id: string, change: (c: CollectionDto) => CollectionDto): CollectionDto[] =>
  collections.map((c) => (c.id === id ? change(c) : c));

/** The user's collections; empty when signed out. Cached for an hour. */
export const getCollections = (forceRefresh = false): Promise<CollectionDto[]> => items.collections.get(forceRefresh);

/** Article key to the ids of the collections that hold it; empty when signed out. Cached for an hour. */
export const getBookmarkedArticles = (forceRefresh = false): Promise<BookmarkedArticles> => items.bookmarked.get(forceRefresh);

export const createCollection = async (name: string): Promise<CollectionDto> => {
  const { collection } = await createCollectionApi.invoke({ name });
  items.collections.set([...(await items.collections.get()), collection]);
  return collection;
};

export const renameCollection = async (id: string, name: string): Promise<void> => {
  await updateCollectionApi.invoke({ id, name });
  items.collections.set(mapCollection(await items.collections.get(), id, (c) => ({ ...c, name })));
};

export const deleteCollection = async (id: string): Promise<void> => {
  await deleteCollectionApi.invoke({ id });
  items.collections.set((await items.collections.get()).filter((c) => c.id !== id));
  // Every article held by it may have lost its last bookmark.
  await items.bookmarked.get(true);
};

export const addArticleToCollection = async (id: string, alias: string, articleId: string): Promise<boolean> => {
  const { added } = await putCollectionArticleApi.invoke({ id, alias, articleId });
  if (!added) {
    return false;
  }
  items.collections.set(mapCollection(await items.collections.get(), id, (c) => ({ ...c, articleCount: c.articleCount + 1 })));
  const bookmarked = { ...(await items.bookmarked.get()) };
  const key = toArticleKey(alias, articleId);
  const held = bookmarked[key] ?? [];
  if (!held.includes(id)) {
    bookmarked[key] = [...held, id];
    items.bookmarked.set(bookmarked);
  }
  return true;
};

export const removeArticleFromCollection = async (id: string, alias: string, articleId: string): Promise<void> => {
  await removeCollectionArticleApi.invoke({ id, alias, articleId });
  items.collections.set(mapCollection(await items.collections.get(), id, (c) => ({ ...c, articleCount: Math.max(0, c.articleCount - 1) })));
  const bookmarked = { ...(await items.bookmarked.get()) };
  const key = toArticleKey(alias, articleId);
  const remaining = (bookmarked[key] ?? []).filter((collectionId) => collectionId !== id);
  if (remaining.length > 0) {
    bookmarked[key] = remaining;
  } else {
    delete bookmarked[key];
  }
  items.bookmarked.set(bookmarked);
};

/** Resolves true when every article was copied. */
export const copyCollection = async (id: string, targetCollectionId: string): Promise<boolean> => {
  const { numArticlesCopied, copiedAll } = await copyCollectionApi.invoke({ id, targetCollectionId });
  items.collections.set(mapCollection(await items.collections.get(), targetCollectionId, (c) => ({ ...c, articleCount: c.articleCount + numArticlesCopied })));
  // Which articles were copied is unknown, so reload the bookmarks in the background.
  void items.bookmarked.get(true);
  return copiedAll;
};

/** The ids of the collections that hold an article, always from the API. */
export const getArticleCollectionIds = async (alias: string, articleId: string): Promise<string[]> =>
  (await getArticleCollectionsApi.invoke({ alias, articleId })).collectionIds;
